---
title: "Volume and Volatility: ATR, Bollinger Bands"
slug: volume-and-volatility
shortTitle: "Volume & Volatility"
description: "How to read volume, measure volatility with ATR and Bollinger Bands, and use both to set stop distances and position sizes that fit the market."
level: intermediate
category: trading
course: intermediate
order: 5
topics: [indicators, risk-management]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/volume-and-volatility.svg
faqs:
  - q: "What is a good ATR value?"
    a: "There is no good or bad ATR; it simply measures how far a market typically moves per candle. It is most useful as a ruler for stop distances and for comparing markets, ideally as a percentage of price."
  - q: "Does a Bollinger Band squeeze tell me which way price will break?"
    a: "No. A squeeze shows that volatility has contracted, and quiet periods are often followed by more active ones, but the bands do not show direction. Traders usually wait for price to break out and close beyond a level before acting."
  - q: "Is volume on a CFD the same as volume on an exchange?"
    a: "Not always. A CFD is priced from a reference feed, so the volume figure you see depends on that feed and may reflect exchange trading or price-update activity rather than CFD trades. Use it to compare a market with its own recent history rather than as an absolute number."
  - q: "Should I use a wider stop when volatility is high?"
    a: "Many traders do, so that normal noise does not hit the stop. The key is to reduce the position size at the same time, so the amount you would lose at the stop stays within your risk limit."
  - q: "Can I see ATR or Bollinger Bands on the trade chart?"
    a: "The trade chart does not currently plot indicators, but you can turn on volume bars and the OHLC legend in the chart settings. The Markets page shows volatility readings, and traders can use a separate charting tool for ATR and Bollinger Bands."
---

Two markets can trend in the same direction and still need completely different handling. One drifts calmly 0.5% a day; the other swings 4% before lunch. **Volatility** is the size of those swings, and it decides how wide your stop must be and how large your position can be. **Volume** is the amount of trading behind a move, and it helps you judge how much conviction a move carries.

This lesson covers what volume can and cannot tell you, two of the most widely used volatility tools, the **Average True Range (ATR)** and **Bollinger Bands**, and how to turn them into practical stop and sizing decisions.

<CTA label="Check volatility on Markets" to="/markets" />

<KeyTakeaways>

- **Volume shows participation.** Moves on rising volume carry more conviction; moves on thin volume are easier to reverse.
- **ATR measures typical range.** It averages how far price travels per candle, including gaps, and works as a ruler for stop distances.
- **Bollinger Bands show relative extremes.** Bands two standard deviations around a 20-period average widen when volatility rises and squeeze when it falls.
- **Higher volatility means smaller size.** A wider stop needs a smaller position so that a loss at the stop stays within your limit.

</KeyTakeaways>

## What volume tells you

**Volume** is how much of a market changed hands during a candle. On a chart it appears as bars under the price, one per candle. Traders use it in three main ways:

- **Confirming breakouts.** Price clearing a level on clearly above-average volume suggests broad participation. A breakout on thin volume is more likely to fail.
- **Spotting climaxes.** A huge volume spike after a long run can mark exhaustion, with late buyers or sellers piling in as the move ends.
- **Reading quiet drift.** Slow, low-volume moves often lack follow-through and reverse easily.

On CFDs, treat volume as relative. A CFD is priced from a reference feed, and the volume you see depends on that feed, so compare a market with its own recent average rather than with another market.

{/* TODO: verify with platform — what the chart's volume bars represent for each asset class (exchange volume or tick activity). */}

## Volatility: how far price typically moves

Volatility is not good or bad; it is a measurement. It also changes. Markets go through quiet stretches and active ones, and volatility tends to cluster: busy days often follow busy days, especially around news such as inflation data, central bank decisions or company earnings. A stop that is comfortable in a quiet week can be hit by ordinary noise in a busy one.

## ATR: the Average True Range

The **true range** of a candle is the largest of three distances:

1. High − low
2. The gap between the high and the previous close
3. The gap between the low and the previous close

Including the previous close means overnight gaps count. The **ATR** is the average true range over a period, usually 14 candles.

<Example title="True range and ATR on GOLD/USDT (illustrative prices)">

Yesterday closed at 2,390. Today's high is 2,415 and low 2,395.

- High − low: 2,415 − 2,395 = 20
- High − previous close: 2,415 − 2,390 = 25
- Low − previous close: 2,395 − 2,390 = 5
- True range: the largest, **25 USDT**

If the last five true ranges were 24, 18, 30, 22 and 26, a simple 5-day ATR would be (24 + 18 + 30 + 22 + 26) ÷ 5 = 120 ÷ 5 = **24 USDT**.

</Example>

Standard ATR uses a smoothed 14-period average rather than a plain one, but the idea is the same: it tells you how much ground the market usually covers per candle.

### Using ATR for stops and size

Many traders place stops a multiple of ATR away from entry, such as 1.5 or 2 ATR, so ordinary noise is less likely to reach them. Then they size the position from that distance, as covered in [Position Sizing](/academy/article/position-sizing).

<Example title="An ATR-based stop on gold (illustrative prices)">

You plan to buy GOLD/USDT at 2,400 with a daily ATR of 24. You choose a stop 1.5 ATR away and are willing to risk 75 USDT.

- Stop distance: 1.5 × 24 = 36, so the stop goes at 2,400 − 36 = **2,364**
- Size: 75 ÷ 36 = 2.08 oz
- With 1 lot = 100 oz and a step of 0.01 lots (1 oz), round down to **0.02 lots** (2 oz)
- Risk at the stop: 2 × 36 = **72 USDT**, before costs

</Example>

{/* TODO: verify with platform — gold lot step; use the Min / step shown in the order panel. */}

<ProTip>

Express ATR as a percentage of price to compare markets. A gold ATR of 24 at 2,400 is 1%; a Bitcoin ATR of 1,800 at 60,000 is 3%. The same stop in percent would be far tighter on Bitcoin, relative to its normal moves, than on gold.

</ProTip>

## Bollinger Bands

**Bollinger Bands** place a line above and below a moving average:

- **Middle band**: a 20-period simple moving average
- **Upper band**: middle + 2 standard deviations
- **Lower band**: middle − 2 standard deviations

A **standard deviation** measures how spread out recent closes are around their average. When prices swing widely, it grows and the bands widen; when prices are calm, the bands contract.

<Example title="Bollinger Bands on ETH/USDT (illustrative prices)">

The 20-day average is 3,000 and the standard deviation of the last 20 closes is 60.

- Upper band: 3,000 + 2 × 60 = **3,120**
- Lower band: 3,000 − 2 × 60 = **2,880**
- Band width: (3,120 − 2,880) ÷ 3,000 = 240 ÷ 3,000 = **8%** of price

If a few weeks later the width has shrunk to 3%, volatility has contracted sharply: a **squeeze**.

</Example>

Traders read the bands in a few ways. In a range, touches of the outer bands can mark stretched prices, and some traders look for a drift back towards the middle. In a strong trend, price can "walk the band", closing near the upper or lower band candle after candle, so a touch alone is not a reversal signal. A squeeze shows quiet conditions that often precede a bigger move, but not its direction. [Moving Averages](/academy/article/moving-averages) explains the middle band in more detail.

<CompareTable caption="Volume, ATR and Bollinger Bands compared">

| | Volume | ATR | Bollinger Bands |
| --- | --- | --- | --- |
| Measures | Trading activity per candle | Typical candle range, including gaps | Spread of closes around an average |
| Shows direction? | No | No | No |
| Common use | Confirming breakouts and climaxes | Stop distance and size | Relative extremes and squeezes |
| Main limit | Depends on the data feed | Lags sudden changes | Bands do not cap price |

</CompareTable>

<Warning>

Volatility rises sharply around major news, and prices can gap past your stop. A stop loss closes at the next available price, so in a fast market the loss can be larger than planned. With leverage, those larger moves hit your account harder. When volatility is high, reduce size, widen stops only together with a smaller position, and consider staying out around scheduled events.

</Warning>

## Volume and volatility on {{BRAND}}

The trade chart does not currently plot indicators, but it helps in other ways. In the chart settings (the gear icon) on desktop, **Volume bars** adds a volume pane under the price, and **OHLC legend** shows each candle's open, high, low and close, which is all you need to work out true range yourself.

<Screenshot name="chart-settings-volume" alt="Chart settings with Volume bars and OHLC legend switched on" caption="Volume bars and the OHLC legend are switched on in the chart settings." />

On the **Markets** page, the quick view stats include figures such as **24h range**, **Volume 24h** and volume compared with its usual level, and the **Opportunity scanner** has a **Volatility** screen for markets whose latest hourly range is far above their recent average. A market's own page also shows **Volatility (30 days)**.

{/* TODO: verify with platform — trade chart has no indicators; quick view stat names, Volatility screen definition, and the asset page Key figures. */}

For ATR and Bollinger Bands, you can use a separate charting tool and place your orders on {{BRAND}}, or estimate ATR by hand:

<Steps>
<Step title="Set the timeframe">

Choose the timeframe you trade on, for example **1D**, and turn on the **OHLC legend** in the chart settings.

</Step>
<Step title="Read the recent candles">

Hover over each of the last 10 to 14 candles and note the high, the low and the previous candle's close.

</Step>
<Step title="Work out each true range">

For each candle, take the largest of high − low, high − previous close and low − previous close.

</Step>
<Step title="Average them">

Add the true ranges and divide by the number of candles. That is your approximate ATR for setting stop distances.

</Step>
</Steps>

## Conclusion

Volume tells you how much participation sits behind a move; volatility tells you how far price usually travels. ATR turns volatility into a practical ruler for stop distances, and Bollinger Bands show when price is stretched relative to recent behavior or when conditions have gone unusually quiet. None of them shows direction. Their real value is in risk control: when the market gets more volatile, give trades more room and cut your size so each loss stays the same in USDT.
